import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, CheckCircle, Clock, MessageCircle, ArrowRight, Star, Lightbulb, Award } from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' } }),
};

const SchoolProjectPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'School Project Help | Science Exhibition Models & Project Files | AGO Innovations',
    description:
      'Expert school project help for Class 6–12 students. Science exhibition models, project files, school assignments, and holiday homework — written and designed by experts. Fast delivery.',
    keywords:
      'school project help, science exhibition model, school project ideas, school project file writing, holiday homework help, school assignment help India, science model for school, school project maker',
    canonical: 'https://agoinnovations.in/school-project-help',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'School Project Help Service',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert school project help for Class 6–12. Science models, project files, and holiday homework.',
      areaServed: 'IN',
      serviceType: 'School Academic Help',
    },
  });

  const services = [
    { icon: <Lightbulb className="w-6 h-6" />, title: 'Science Exhibition Projects', desc: 'Working models and project files for school science fairs and exhibitions (Class 6–12).' },
    { icon: <BookOpen className="w-6 h-6" />, title: 'Project File Writing', desc: 'Neatly written project files for all subjects — Science, Social Studies, History, and more.' },
    { icon: <Star className="w-6 h-6" />, title: 'Holiday Homework Help', desc: 'Complete holiday homework assistance — creative and school-standard quality.' },
    { icon: <Award className="w-6 h-6" />, title: 'School Assignments', desc: 'Subject-specific school assignments for Class 9, 10, 11, and 12 students.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Annual Day Preparation', desc: 'Project boards, displays, and content for school annual days and exhibitions.' },
    { icon: <CheckCircle className="w-6 h-6" />, title: 'Practical Files', desc: 'Physics, Chemistry, Biology, and Computer Science practical files — properly formatted.' },
  ];

  const faqs = [
    { q: 'Which classes do you cover for school project help?', a: 'We cover Class 6 to Class 12, across CBSE, ICSE, and State Board curricula for all major subjects.' },
    { q: 'Do you make physical science exhibition models?', a: 'We help with project documentation, write-ups, and design guidance for science exhibition models. Physical model making depends on location — contact us for local availability.' },
    { q: 'How quickly can a project file be delivered?', a: 'Most school project files are delivered within 24–48 hours. Urgent requirements are handled faster on request.' },
    { q: 'What subjects do you cover for school projects?', a: 'Science, Social Studies, History, Geography, English, Computer Science, Mathematics, and more — all major school subjects covered.' },
    { q: 'Can you help with CBSE and ICSE project formats?', a: 'Yes. We follow the specific format and guidelines of CBSE, ICSE, SSC, HSC, and other boards as per your requirement.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-emerald-900 via-green-900 to-emerald-800 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 max-w-4xl mx-auto">
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">🏫 Student Services — School Projects</span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">School Project Help<span className="block text-yellow-400">Class 6–12</span></h1>
          <p className="text-emerald-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">Science exhibition models, project files, holiday homework, and school assignments — written by expert academic writers. CBSE, ICSE, and State Board covered.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/learn/customer-work')} className="bg-yellow-400 hover:bg-yellow-300 text-emerald-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2">Submit Requirement <ArrowRight className="w-5 h-5" /></motion.button>
            <a href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20school%20project%20help." target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp Us</a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-emerald-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Class 6–12 Covered</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> CBSE, ICSE, State Board</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 24–48 Hour Delivery</span>
          </div>
        </motion.div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">School Project Services We Offer</h2>
            <p className="text-gray-500 text-lg">Everything a school student needs — science models to project files.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 mb-4">{svc.icon}</div>
                <h3 className="font-black text-gray-900 text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">School Project FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-br from-emerald-800 to-green-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Need School Project Help?</h2>
          <p className="text-emerald-200 mb-8">Submit your project details and get expert help delivered within 24–48 hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/learn/customer-work')} className="bg-yellow-400 hover:bg-yellow-300 text-emerald-900 font-bold px-8 py-4 rounded-full transition-colors">Submit Requirement</button>
            <button onClick={() => navigate('/college-project-help')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">College Projects</button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default SchoolProjectPage;
